import React, {useState} from "react";
import {
    Box,
    Flex,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    FormControl,
    FormLabel,
    FormErrorMessage,
    Input,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Portal,
    Select,
    Skeleton,
    useDisclosure,
    useToast, Icon,
} from "@chakra-ui/react";
import {
    useReactTable,
    getCoreRowModel,
    flexRender,
} from "@tanstack/react-table";
import axios from "axios";
import Card from "components/card/Card";
import {MdBusiness, MdMoreHoriz, MdOutlineDelete, MdOutlineEdit, MdPeopleOutline} from "react-icons/md";

export default function ColumnsTable({columnsData, tableData, onDataUpdate, isLoading: isTableLoading}) {
    const {isOpen, onOpen, onClose} = useDisclosure();
    const {isOpen: isOpenDelete, onOpen: onOpenDelete, onClose: onCloseDelete} = useDisclosure();
    const {
        isOpen: isOpenAdditionalCompany,
        onOpen: onOpenAdditionalCompany,
        onClose: onCloseAdditionalCompany
    } = useDisclosure();
    const [selectedRow, setSelectedRow] = useState(null);
    const [editData, setEditData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState("");
    const [isCompaniesLoading, setIsCompaniesLoading] = useState(false);
    const [isAssigningCompany, setIsAssigningCompany] = useState(false);
    const toast = useToast();

    const textColor = useColorModeValue("gray.700", "gray.200");
    const headerTextColor = useColorModeValue("gray.600", "gray.200");
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const headerBgColor = useColorModeValue("gray.50", "gray.700");
    const tableBodyBg = useColorModeValue("white", "gray.800");
    const mutedTextColor = useColorModeValue("secondaryGray.600", "secondaryGray.500");
    const rowHoverBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
    const modalBg = useColorModeValue("white", "navy.800");
    const fieldBg = useColorModeValue("white", "navy.700");

    // Setup TanStack Table
    const table = useReactTable({
        data: tableData || [],
        columns: columnsData,
        getCoreRowModel: getCoreRowModel(),
    });

    const handleEditClick = (rowData) => {
        setSelectedRow(rowData);
        setEditData({
            nrp: rowData.nrp || "",
            email: rowData.email || "",
            name: rowData.name || ""
        });
        onOpen();
    };

    const handleDeleteClick = (rowData) => {
        setSelectedRow(rowData);
        onOpenDelete();
    };

    const clearAuthentication = () => {
        document.cookie.split(";").forEach((cookie) => {
            document.cookie = cookie.trim().split("=")[0] + "=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/";
        });
        window.location.reload();
    };

    const handleAdditionalCompanyClick = async (rowData) => {
        setSelectedRow(rowData);
        setSelectedCompanyId("");
        setCompanies([]);
        onOpenAdditionalCompany();
        setIsCompaniesLoading(true);

        try {
            const response = await axios.get(
                process.env.REACT_APP_API_URL + "/v1/company/dropdown",
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (response.data?.success) {
                setCompanies(Array.isArray(response.data.data) ? response.data.data : []);
            } else {
                toast({
                    title: "Unable to load companies",
                    description: response.data?.message || "An error occurred",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                    position: "top-right",
                });
            }
        } catch (error) {
            if (error.response?.status === 401 || error.response?.status === 403) {
                clearAuthentication();
                return;
            }

            toast({
                title: "Unable to load companies",
                description: error.response?.data?.message || "An error occurred",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "top-right",
            });
        } finally {
            setIsCompaniesLoading(false);
        }
    };

    const handleAdditionalCompanyClose = () => {
        if (isAssigningCompany) {
            return;
        }

        setSelectedCompanyId("");
        setCompanies([]);
        onCloseAdditionalCompany();
    };

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setEditData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const token = document.cookie.split("; ").find(row => row.startsWith("token=")).split("=")[1];

    const selectedCompany = companies.find((company) => String(company.key) === selectedCompanyId);
    const companyId = Number(selectedCompany?.key);
    const userId = Number(selectedRow?.id);
    const isPrimaryCompany = selectedRow?.companyId !== undefined &&
        selectedRow?.companyId !== null &&
        Number(selectedRow.companyId) === companyId;

    const handleAdditionalCompanySubmit = async () => {
        if (!Number.isInteger(userId) || userId <= 0 || !Number.isInteger(companyId) || companyId <= 0) {
            toast({
                title: "Invalid selection",
                description: "Select a valid user and company.",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "top-right",
            });
            return;
        }

        if (isPrimaryCompany) {
            return;
        }

        setIsAssigningCompany(true);
        try {
            const response = await axios.put(
                process.env.REACT_APP_API_URL + `/v1/users/${userId}/additional-companies/${companyId}`,
                undefined,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            toast({
                title: "Additional company assigned",
                description: response.data?.message || "Additional company assigned successfully",
                status: "success",
                duration: 3000,
                isClosable: true,
                position: "top-right",
            });
            setSelectedCompanyId("");
            setCompanies([]);
            onCloseAdditionalCompany();
        } catch (error) {
            if (error.response?.status === 401 || error.response?.status === 403) {
                clearAuthentication();
                return;
            }

            toast({
                title: "Assignment failed",
                description: error.response?.data?.message || "An error occurred",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "top-right",
            });
        } finally {
            setIsAssigningCompany(false);
        }
    };

    const handleUpdate = async () => {
        setIsLoading(true);
        try {
            // Format request body sesuai kebutuhan
            const requestBody = {
                nrp: editData.nrp,
                email: editData.email,
                name: editData.name
            };

            const response = await axios.patch(
                process.env.REACT_APP_API_URL + `/v1/update`,
                requestBody,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            if (response.status === 200) {
                toast({
                    title: "Update successful",
                    description: "Data has been updated successfully",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                    position: "top-right",
                });
                onDataUpdate(); // Panggil callback untuk memberitahu komponen induk bahwa data telah berubah
                onClose();
            }
        } catch (error) {
            toast({
                title: "Update failed",
                description: error.response?.data?.message || "An error occurred",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "top-right",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        setIsLoading(true);
        try {
            // Format request body sesuai kebutuhan
            const requestBody = {
                nrp: selectedRow.nrp,
                email: selectedRow.email,
            };

            const response = await axios.delete(process.env.REACT_APP_API_URL + `/v1/users/delete`, {
                data: requestBody,
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (response.status === 200) {
                toast({
                    title: "Delete successful",
                    description: "Data has been deleted successfully",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                    position: "top-right",
                });
                onDataUpdate(); // Panggil callback untuk memberitahu komponen induk bahwa data telah berubah
                onCloseDelete();
            }
        } catch (error) {
            toast({
                title: "Delete failed",
                description: error.response?.data?.message || "An error occurred",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: "top-right",
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Card p="0" mb="24px" overflow="hidden">
                <Flex px={{base: 4, md: 6}} py={5} justify="space-between" align="center" borderBottomWidth="1px" borderColor={borderColor}>
                    <Box>
                        <Text color={textColor} fontSize="lg" fontWeight="700">Daftar User</Text>
                        <Text color={mutedTextColor} fontSize="sm" mt={1}>Pilih menu aksi untuk mengelola data user.</Text>
                    </Box>
                    <Text color={mutedTextColor} fontSize="sm" fontWeight="600">
                        {isTableLoading ? "Memuat data..." : `${table.getRowModel().rows.length} user`}
                    </Text>
                </Flex>
                <Box overflowX="auto">
                    <Table variant="simple" bg={tableBodyBg} minW="960px">
                        <Thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <Tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <Th
                                            key={header.id}
                                            color={headerTextColor}
                                            py={4}
                                            fontSize="xs"
                                            letterSpacing="0.06em"
                                            textTransform="uppercase"
                                            bg={headerBgColor}
                                            borderColor={borderColor}
                                        >
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                        </Th>
                                    ))}
                                    <Th
                                        color={headerTextColor}
                                        py={4}
                                        fontSize="xs"
                                        letterSpacing="0.06em"
                                        textTransform="uppercase"
                                        bg={headerBgColor}
                                        borderColor={borderColor}
                                        textAlign="right"
                                    >
                                        Actions
                                    </Th>
                                </Tr>
                            ))}
                        </Thead>
                        <Tbody>
                            {isTableLoading ? (
                                Array.from({length: 5}).map((_, rowIndex) => (
                                    <Tr key={rowIndex}>
                                        {Array.from({length: columnsData.length + 1}).map((__, cellIndex) => (
                                            <Td key={cellIndex} py={4} borderColor={borderColor}>
                                                <Skeleton height="16px" borderRadius="full"/>
                                            </Td>
                                        ))}
                                    </Tr>
                                ))
                            ) : table.getRowModel().rows.length > 0 ? (
                                table.getRowModel().rows.map((row) => (
                                    <Tr key={row.id} transition="background 0.2s ease" _hover={{bg: rowHoverBg}}>
                                        {row.getVisibleCells().map((cell) => (
                                            <Td
                                                key={cell.id}
                                                py={4}
                                                color={textColor}
                                                fontSize="sm"
                                                borderColor={borderColor}
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </Td>
                                        ))}
                                        <Td py={3} borderColor={borderColor} textAlign="right">
                                            <Menu placement="bottom-end">
                                                <MenuButton
                                                    as={Button}
                                                    variant="light"
                                                    size="sm"
                                                    aria-label={`Aksi untuk ${row.original.name || row.original.email}`}
                                                    rightIcon={<Icon as={MdMoreHoriz} boxSize={5}/>}
                                                >
                                                    Aksi
                                                </MenuButton>
                                                <Portal>
                                                    <MenuList minW="190px" p={2}>
                                                        <MenuItem borderRadius="8px" icon={<Icon as={MdOutlineEdit} boxSize={4}/>} onClick={() => handleEditClick(row.original)}>
                                                            Edit user
                                                        </MenuItem>
                                                        <MenuItem borderRadius="8px" icon={<Icon as={MdBusiness} boxSize={4}/>} onClick={() => handleAdditionalCompanyClick(row.original)}>
                                                            Tambah company
                                                        </MenuItem>
                                                        <MenuItem borderRadius="8px" color="red.500" icon={<Icon as={MdOutlineDelete} boxSize={4}/>} onClick={() => handleDeleteClick(row.original)}>
                                                            Hapus user
                                                        </MenuItem>
                                                    </MenuList>
                                                </Portal>
                                            </Menu>
                                        </Td>
                                    </Tr>
                                ))
                            ) : (
                                <Tr>
                                    <Td colSpan={columnsData.length + 1} borderColor={borderColor}>
                                        <Flex direction="column" align="center" justify="center" py={12} gap={3}>
                                            <Flex w="48px" h="48px" borderRadius="full" bg={rowHoverBg} align="center" justify="center">
                                                <Icon as={MdPeopleOutline} boxSize={6} color="brand.500"/>
                                            </Flex>
                                            <Box textAlign="center">
                                                <Text color={textColor} fontWeight="700">User tidak ditemukan</Text>
                                                <Text color={mutedTextColor} fontSize="sm" mt={1}>Ubah atau reset filter untuk melihat data user.</Text>
                                            </Box>
                                        </Flex>
                                    </Td>
                                </Tr>
                            )}
                        </Tbody>
                    </Table>
                </Box>
            </Card>

            <Modal isOpen={isOpen} onClose={onClose} isCentered>
                <ModalOverlay/>
                <ModalContent bg={modalBg} borderRadius="20px">
                    <ModalHeader borderBottomWidth="1px" borderColor={borderColor}>Edit User</ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody py={6}>
                        <FormControl mb={4}>
                            <FormLabel>NRP</FormLabel>
                            <Input
                                name="nrp"
                                value={editData.nrp}
                                onChange={handleInputChange}
                                bg={fieldBg}
                            />
                        </FormControl>
                        <FormControl mb={4}>
                            <FormLabel>Email</FormLabel>
                            <Input
                                name="email"
                                value={editData.email}
                                onChange={handleInputChange}
                                bg={fieldBg}
                            />
                        </FormControl>
                        <FormControl mb={4}>
                            <FormLabel>Name</FormLabel>
                            <Input
                                name="name"
                                value={editData.name}
                                onChange={handleInputChange}
                                bg={fieldBg}
                            />
                        </FormControl>
                    </ModalBody>

                    <ModalFooter borderTopWidth="1px" borderColor={borderColor}>
                        <Button variant="ghost" mr={3} onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="blue"
                            onClick={handleUpdate}
                            isLoading={isLoading}
                        >
                            Save Changes
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            <Modal
                isOpen={isOpenAdditionalCompany}
                onClose={handleAdditionalCompanyClose}
                closeOnOverlayClick={!isAssigningCompany}
                closeOnEsc={!isAssigningCompany}
                isCentered
            >
                <ModalOverlay/>
                <ModalContent bg={modalBg} borderRadius="20px">
                    <ModalHeader borderBottomWidth="1px" borderColor={borderColor}>Tambah Additional Company</ModalHeader>
                    <ModalCloseButton isDisabled={isAssigningCompany}/>
                    <ModalBody py={6}>
                        <Box mb={5} p={4} bg={rowHoverBg} borderRadius="12px">
                            <Text color={textColor} fontWeight="700">{selectedRow?.name}</Text>
                            <Text color={mutedTextColor} fontSize="sm" mt={1}>{selectedRow?.email || selectedRow?.nrp}</Text>
                        </Box>
                        <FormControl isRequired isInvalid={isPrimaryCompany}>
                            <FormLabel htmlFor="additional-company">Company</FormLabel>
                            <Select
                                id="additional-company"
                                placeholder={isCompaniesLoading ? "Loading companies..." : "Select company"}
                                value={selectedCompanyId}
                                onChange={(event) => setSelectedCompanyId(event.target.value)}
                                isDisabled={isCompaniesLoading || isAssigningCompany}
                                bg={fieldBg}
                            >
                                {companies.map((company) => (
                                    <option key={company.key} value={company.key}>
                                        {company.value}
                                    </option>
                                ))}
                            </Select>
                            {isPrimaryCompany && (
                                <FormErrorMessage>
                                    Company is already the user&apos;s primary company.
                                </FormErrorMessage>
                            )}
                        </FormControl>
                        {!isCompaniesLoading && companies.length === 0 && (
                            <Text mt={2} color={textColor}>
                                No company is available.
                            </Text>
                        )}
                    </ModalBody>
                    <ModalFooter borderTopWidth="1px" borderColor={borderColor}>
                        <Button variant="ghost" mr={3} onClick={handleAdditionalCompanyClose} isDisabled={isAssigningCompany}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="teal"
                            onClick={handleAdditionalCompanySubmit}
                            isLoading={isAssigningCompany}
                            isDisabled={!selectedCompanyId || isPrimaryCompany || isCompaniesLoading}
                        >
                            Assign Company
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            <Modal isOpen={isOpenDelete} onClose={onCloseDelete} isCentered>
                <ModalOverlay/>
                <ModalContent bg={modalBg} borderRadius="20px">
                    <ModalHeader borderBottomWidth="1px" borderColor={borderColor}>Hapus User</ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody py={6}>
                        <Text color={textColor}>
                            Anda akan menghapus data user dengan NRP <Text as="span" fontWeight="700">{selectedRow?.nrp}</Text>. Tindakan ini tidak dapat dibatalkan.
                        </Text>
                    </ModalBody>

                    <ModalFooter borderTopWidth="1px" borderColor={borderColor}>
                        <Button variant="ghost" mr={3} onClick={onCloseDelete}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="red"
                            onClick={handleDelete}
                            isLoading={isLoading}
                        >
                            Delete
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}
